import React from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { cn } from "~/lib/cn";
import { Card, CardContent } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { AuthorCard } from "~/components/ui/author-card";
import { createServerFn } from "@tanstack/react-start";
import { dbMiddleware } from "~/lib/db-middleware";
import { getActiveSummit, getSummitWithDays } from "~/lib/server/summit";

// Helper function to convert time string to minutes for sorting
function timeToMinutes(timeString: string): number {
  const [time, period] = timeString.split(" ");
  const [hours, minutes] = time.split(":").map(Number);

  let totalMinutes = (hours === 12 ? 0 : hours) * 60 + (minutes || 0);
  if (period.toUpperCase() === "PM" && hours !== 12) {
    totalMinutes += 12 * 60;
  }

  return totalMinutes;
}

// Public summit data fetcher (no auth required)
export const getPublicSummitData = createServerFn({ method: "GET" })
  .middleware([dbMiddleware])
  .handler(async () => {
    const activeSummit = await getActiveSummit();
    if (!activeSummit) {
      return null;
    }

    const summitData = await getSummitWithDays(activeSummit.id);
    if (!summitData) {
      return null;
    }

    // Return only public information
    return {
      summit: {
        id: summitData.summit.id,
        title: summitData.summit.title,
        startDate: summitData.summit.startDate,
        endDate: summitData.summit.endDate,
      },
      days: summitData.days.map((day) => ({
        id: day.id,
        date: day.date,
        title: day.title,
        description: day.description,
        sessions: day.sessions
          .map((session) => ({
            id: session.id,
            title: session.title,
            description: session.description,
            speaker: {
              id: session.speaker.id,
              name: session.speaker.name,
              title: session.speaker.title,
              avatarUrl: session.speaker.avatarUrl,
            },
            date: session.date,
            startTime: session.startTime,
            endTime: session.endTime,
            topics: session.topics,
            isVipOnly: session.isVipOnly,
          }))
          .sort(
            (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
          ),
      })),
    };
  });

interface PublicSessionCardProps {
  session: {
    id: string;
    title: string;
    description: string;
    speaker: {
      id: string;
      name: string;
      title: string;
      avatarUrl: string;
    };
    startTime: string;
    endTime: string;
    topics: string[];
    isVipOnly?: boolean;
  };
  showTime?: boolean;
}

const PublicSessionCard: React.FC<PublicSessionCardProps> = ({
  session,
  showTime = false,
}) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardContent>
        <div className="flex gap-6">
          {/* Time column - only if showTime is true */}
          {showTime && (
            <div className="flex w-24 shrink-0 flex-col text-gray-600">
              <div className="text-lg font-medium">{session.startTime}</div>
              <div className="text-xs text-gray-500">
                {session.endTime && `- ${session.endTime}`}
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="flex-1 space-y-3">
            {/* Speaker */}
            <AuthorCard author={session.speaker} variant="compact" />

            {/* Title */}
            <h3 className="font-heading text-xl font-semibold leading-tight text-gray-900">
              {session.title}
            </h3>

            {/* Description */}
            <p className="text-sm text-gray-600 line-clamp-3">
              {session.description}
            </p>

            {/* Tags and badges */}
            <div className="flex flex-wrap items-center gap-2">
              {session.topics.map((topic) => (
                <Badge key={topic} variant="secondary" size="sm">
                  {topic}
                </Badge>
              ))}
              {session.isVipOnly && (
                <Badge variant="pro" size="sm">
                  VIP Only
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export interface DailySummitAgendaProps {
  title?: string;
  showTimes?: boolean;
  description?: string;
  className?: string;
}

export const DailySummitAgenda: React.FC<DailySummitAgendaProps> = ({
  title = "Summit Agenda",
  showTimes = false,
  description,
  className,
}) => {
  const { data: summitData, isLoading } = useQuery({
    queryKey: ["summit", "public"],
    queryFn: () => getPublicSummitData(),
  });

  if (isLoading) {
    return (
      <div className={cn("w-full py-12", className)}>
        <div className="text-center text-gray-600">
          Loading summit agenda...
        </div>
      </div>
    );
  }

  if (!summitData) {
    return (
      <div className={cn("w-full py-12", className)}>
        <div className="text-center text-gray-600">
          No summit data available
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-8", className)}>
      {/* Header */}
      <div className="text-center">
        <h2 className="font-heading text-3xl text-text-primary tracking-tight mb-2">
          {title}
        </h2>
        {description && (
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {description}
          </p>
        )}
      </div>

      {/* Days */}
      <div className="space-y-12">
        {summitData.days.map((day) => (
          <div key={day.id} className="space-y-6">
            {/* Day Header */}
            <div className="text-center pb-6 border-b border-gray-200">
              <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-2">
                {day.title}
              </h3>
              <p className="text-gray-600">
                {format(new Date(day.date), "EEEE, MMMM d, yyyy")}
                {day.sessions.length > 0 &&
                  ` • ${day.sessions.length} sessions`}
              </p>
              {day.description && (
                <p className="text-sm text-gray-500 mt-2 max-w-2xl mx-auto">
                  {day.description}
                </p>
              )}
            </div>

            {/* Sessions */}
            <div className="space-y-4">
              {day.sessions.map((session) => (
                <PublicSessionCard
                  key={session.id}
                  session={session}
                  showTime={showTimes}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
